import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc as firestoreDoc, updateDoc, setDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getCypAdhdReportContentPathSegments } from '../constants/cypAdhdFirestorePaths';
import { STAGE_TO_REPORT_PREFIX } from '../constants/cypAdhdStages';

// The Rating Scales appendix is the only render stage that has no seeded
// reportContent doc: the patient builder seeds CI/SSI/DD/PAD docs, and the
// patientReport doc is created by this trigger's transaction (or by the CF).
// Rating Scales skips the transactional guard entirely.
const STAGES_WITHOUT_TRANSACTIONAL_GUARD = new Set(['ratingScales']);

/**
 * Trigger CYP ADHD document rendering for one assessment stage.
 *
 * The CYP counterpart to TriggerRenderAutismDocTemplate.js, byte-for-byte in
 * behaviour: status lives on the modular reportContent doc as
 * {prefix}ReportProcessingStatus, one generic Cloud Function
 * (renderAndStoreCypAdhdDocument) dispatches on stage_key, and a transaction
 * guards against a second call clobbering a just-arrived 'complete'.
 * {prefix}ReportProcessingStartedAt is stamped so stuck-processing detection
 * has an anchor.
 *
 * A deadline-exceeded from the client does NOT flip the status to error: the
 * function is still running server-side and will set its own terminal state.
 *
 * @param {Object} params
 * @param {string} params.patientId   - Patient Firestore document ID.
 * @param {string} params.stageKey    - 'clinicalInterview' |
 *                                      'semiStructuredInterview' |
 *                                      'diagnosticDecision' |
 *                                      'postAssessmentDiscussion' |
 *                                      'patientReport'.
 * @param {string} params.clinicianId - UID of the triggering clinician (must
 *                                      equal the authenticated user).
 * @returns {Promise<{success: boolean, data?: object, error?: any}>}
 */
export const TriggerRenderCypAdhdDocTemplate = async ({ patientId, stageKey, clinicianId }) => {
  const renderAndStoreCypAdhdDocument = httpsCallable(functions, 'renderAndStoreCypAdhdDocument', {
    timeout: 360000, // 6 minutes (slightly longer than the 5-minute server timeout)
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!patientId || !stageKey || !clinicianId) {
    console.error('Missing required parameters:', { patientId, stageKey, clinicianId });
    return { success: false, error: 'Missing required parameters' };
  }

  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) {
    console.error('Unknown CYP ADHD stage key:', stageKey);
    return { success: false, error: 'Unknown stage key' };
  }

  const statusField = `${prefix}ReportProcessingStatus`;
  const startedAtField = `${prefix}ReportProcessingStartedAt`;

  try {
    const reportContentRef = firestoreDoc(
      db, ...getCypAdhdReportContentPathSegments(patientId, stageKey),
    );

    // Rating Scales has no seeded reportContent doc and no transactional
    // guard: the CF creates the doc and is authoritative on status.
    if (STAGES_WITHOUT_TRANSACTIONAL_GUARD.has(stageKey)) {
      try {
        const result = await renderAndStoreCypAdhdDocument({
          patient_id: patientId,
          stage_key: stageKey,
          clinician_id: clinicianId,
        });
        return { success: true, data: result.data };
      } catch (error) {
        if (error.code === 'deadline-exceeded') {
          return { success: true, data: { status: 'processing' } };
        }
        console.error(`Error in renderAndStoreCypAdhdDocument (${stageKey}):`, error);
        return { success: false, error: error.message || 'Report generation failed' };
      }
    }

    // ── Transactional guard ──────────────────────────────────────────────
    // Prevents duplicate CF invocations when concurrent callers attempt to
    // render the same document simultaneously (mirrors the Autism pattern
    // in TriggerRenderAutismPatientReport.js).
    //
    // For the Patient Report specifically, we also write the trigger-time
    // anchor (patientReportReportApprovedAt) so the auto-helper's staleness
    // check detects section changes that occur while the CF is running.
    // The render CF intentionally does NOT write this anchor on success —
    // the client owns it. This is what prevents the auto-helper from
    // looping: the anchor advances past every section's approval timestamp.
    let alreadyProcessing = false;

    const isPatientReport = stageKey === 'patientReport';

    try {
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(reportContentRef);

        if (snap.exists()) {
          const current = snap.data()?.[statusField];
          if (current === 'processing') {
            alreadyProcessing = true;
            return;
          }

          const updates = {
            [statusField]: 'processing',
            [startedAtField]: serverTimestamp(),
          };
          // Trigger-time anchor for the Patient Report only.
          if (isPatientReport) {
            updates.patientReportReportApprovedAt = serverTimestamp();
            updates.patientReportRenderIntent = { clinicianId };
          }
          transaction.update(reportContentRef, updates);
        } else {
          // The reportContent doc does not exist yet (e.g. first Patient
          // Report render on a patient created before the builder seeded it).
          // Create it with merge so we don't clobber any concurrent write.
          const initialDoc = {
            [statusField]: 'processing',
            [startedAtField]: serverTimestamp(),
          };
          if (isPatientReport) {
            initialDoc.patientReportReportApprovedAt = serverTimestamp();
            initialDoc.patientReportRenderIntent = { clinicianId };
          }
          transaction.set(reportContentRef, initialDoc, { merge: true });
        }
      }, { maxAttempts: 10 });
    } catch (txnError) {
      // If the transaction fails (e.g. doc doesn't exist and rules forbid
      // creates), fall back to a setDoc+merge outside the transaction for
      // the patientReport doc only.
      if (isPatientReport) {
        try {
          await setDoc(reportContentRef, {
            [statusField]: 'processing',
            [startedAtField]: serverTimestamp(),
            patientReportReportApprovedAt: serverTimestamp(),
            patientReportRenderIntent: { clinicianId },
          }, { merge: true });
        } catch (fallbackError) {
          console.error('Fallback setDoc for patientReport failed:', fallbackError);
          return { success: false, error: fallbackError };
        }
      } else {
        throw txnError;
      }
    }

    if (alreadyProcessing) {
      console.log(`Skipping ${stageKey} CYP render — already processing`);
      return { success: true, data: { status: 'already-processing' } };
    }

    // Fire and forget: the CF flips the status to 'complete' or 'error' itself.
    renderAndStoreCypAdhdDocument({
      patient_id: patientId,
      stage_key: stageKey,
      clinician_id: clinicianId,
    }).then((result) => {
      console.log('CYP ADHD document generated successfully:', result.data);
    }).catch((error) => {
      console.error('Error in renderAndStoreCypAdhdDocument function:', error);
      if (error.code === 'deadline-exceeded') {
        console.log('Client-side timeout occurred, but function continues processing in background');
        return;
      }
      updateDoc(reportContentRef, {
        [statusField]: 'error',
      }).catch((updateError) => {
        console.error('Error updating CYP processing status:', updateError);
      });
    });

    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error setting CYP processing status:', error);
    return { success: false, error };
  }
};
